import { API_ROUTES } from './apiRoutes';

export const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
let csrfToken = null;

const RETRYABLE_GET_STATUSES = new Set([408, 425, 429, 500, 502, 503, 504]);
const GET_RETRY_DELAYS_MS = [0, 350, 1000];

function wait(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function fetchWithRetry(url, options = {}) {
  const method = String(options.method || 'GET').toUpperCase();
  const attempts = method === 'GET' ? GET_RETRY_DELAYS_MS.length : 1;
  let lastError = null;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    if (GET_RETRY_DELAYS_MS[attempt] > 0) await wait(GET_RETRY_DELAYS_MS[attempt]);
    try {
      const response = await fetch(url, options);
      const retryable = method === 'GET' && RETRYABLE_GET_STATUSES.has(response.status);
      if (!retryable || attempt === attempts - 1) return response;
    } catch (error) {
      lastError = error;
      if (method !== 'GET' || attempt === attempts - 1) throw error;
    }
  }

  throw lastError || new Error('Request failed');
}

export function buildApiUrl(path) {
  if (!path) return API_BASE || '/';
  if (/^https?:\/\//i.test(path)) return path;
  const normalizedPath = path.startsWith('/') ? path : '/' + path;
  return API_BASE + normalizedPath;
}

export function buildWsUrl(path = API_ROUTES.websocket.liveUpdates) {
  const normalizedPath = path.startsWith('/') ? path : '/' + path;
  const base = API_BASE || window.location.origin;
  return base.replace(/^http/i, 'ws') + normalizedPath;
}

export function getAuthToken() {
  return localStorage.getItem('access_token');
}

export function setCsrfToken(token) {
  csrfToken = token || null;
}

export async function getCsrfToken() {
  if (csrfToken) return csrfToken;
  try {
    const res = await fetch(buildApiUrl(API_ROUTES.auth.csrf), {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    if (res.ok) {
      const payload = await res.json();
      csrfToken = payload?.csrf_token || null;
    }
  } catch {
    csrfToken = null;
  }
  return csrfToken;
}

export function getAuthHeaders(extraHeaders = {}) {
  const token = getAuthToken();
  const headers = { ...extraHeaders };
  if (!headers['Content-Type'] && !headers['content-type']) {
    headers['Content-Type'] = 'application/json';
  }
  if (token) headers.Authorization = 'Bearer ' + token;
  return headers;
}

async function buildRequestHeaders(extraHeaders = {}, auth = true, body, method = 'GET') {
  const headers = auth ? getAuthHeaders(extraHeaders) : { ...extraHeaders };
  const isFormData = body instanceof FormData;
  if (!isFormData && !headers['Content-Type'] && !headers['content-type']) {
    headers['Content-Type'] = 'application/json';
  }
  if (isFormData) {
    delete headers['Content-Type'];
    delete headers['content-type'];
  }
  if (auth && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase())) {
    const token = await getCsrfToken();
    if (token) headers['X-CSRF-Token'] = token;
  }
  return headers;
}

export function clearAuthSession() {
  localStorage.removeItem('access_token');
  csrfToken = null;
}

export function handleAuthError(res, path = '') {
  if (res.status !== 401) return;
  clearAuthSession();
  const isSessionProbe = String(path).endsWith('/auth/me') || String(path).endsWith('/auth/csrf');
  if (!isSessionProbe && window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
}

async function parseResponse(res) {
  const contentType = res.headers.get('content-type') || '';
  if (res.status === 204) return null;
  if (contentType.includes('application/json')) return res.json();
  return res.text();
}

export async function apiRequest(path, options = {}) {
  const { auth = true, headers = {}, body, ...rest } = options;
  const method = rest.method || 'GET';
  const requestHeaders = await buildRequestHeaders(headers, auth, body, method);
  const requestBody = body && typeof body === 'object' && !(body instanceof FormData)
    ? JSON.stringify(body)
    : body;

  const res = await fetchWithRetry(buildApiUrl(path), {
    ...rest,
    method,
    credentials: 'include',
    headers: requestHeaders,
    body: requestBody,
  });

  if (auth) handleAuthError(res, path);
  const payload = await parseResponse(res);
  if (!res.ok) {
    const detail = payload?.detail || payload?.error || payload || 'HTTP ' + res.status;
    throw new Error(String(detail));
  }
  return payload;
}

export async function apiFetch(path, options = {}) {
  const { auth = true, headers = {}, body, ...rest } = options;
  const method = rest.method || 'GET';
  const requestHeaders = await buildRequestHeaders(headers, auth, body, method);
  const requestBody = body && typeof body === 'object' && !(body instanceof FormData)
    ? JSON.stringify(body)
    : body;

  const res = await fetchWithRetry(buildApiUrl(path), {
    ...rest,
    method,
    credentials: 'include',
    headers: requestHeaders,
    body: requestBody,
  });

  if (auth) handleAuthError(res, path);
  return res;
}

export const apiGet = (path, options = {}) => apiRequest(path, { ...options, method: 'GET' });
export const apiPost = (path, body, options = {}) => apiRequest(path, { ...options, method: 'POST', body });
export const apiPut = (path, body, options = {}) => apiRequest(path, { ...options, method: 'PUT', body });
export const apiPatch = (path, body, options = {}) => apiRequest(path, { ...options, method: 'PATCH', body });
export const apiDelete = (path, options = {}) => apiRequest(path, { ...options, method: 'DELETE' });
